import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './authConfig';

/**
 * Shared MSAL instance singleton.
 *
 * The React tree consumes MSAL via <MsalProvider instance={msalInstance}> in
 * index.js, but non-React modules (e.g. the API service layer in
 * src/services/*) also need the token. Creating the PublicClientApplication
 * here — once — lets both the provider and plain modules share the SAME
 * instance and its token cache, so acquireTokenSilent reuses cached tokens and
 * refreshes them transparently.
 */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Key under which we stash the B2C ID token at login time.
 *
 * WHY: the Talisis login (Session.setSessionCookie) calls `localStorage.clear()`
 * AND `sessionStorage.clear()`, which wipes MSAL's own account cache — so after
 * login `msalInstance.getAllAccounts()` returns 0 and acquireTokenSilent can't
 * run. To work around that WITHOUT changing the Talisis session flow, AuthScreen
 * captures the token from its `acquireTokenSilent` response and stores it here
 * AFTER the clear has happened, so it survives.
 *
 * We use localStorage (not sessionStorage) so the token survives a full page
 * reload and tab close. Session.setSessionCookie/removeSessionCookie both call
 * `localStorage.clear()`, so this key is explicitly saved-and-restored across
 * the login clear (same pattern as `userRoles`) and dropped on logout. Token
 * lifetime ~1h; see the caveat in getIdToken about refresh.
 *
 * Exported so Session.js can reference the exact same key when it restores the
 * token across its storage clear.
 */
export const PRD_TOKEN_KEY = 'prd_b2c_id_token';

/** Stash the freshly-acquired B2C ID token (called from AuthScreen post-login). */
export function storePrdIdToken(idToken) {
    try {
        if (idToken) localStorage.setItem(PRD_TOKEN_KEY, idToken);
    } catch {
        /* storage may be unavailable; ignore */
    }
}

/** Clear the stashed token (call on logout). */
export function clearPrdIdToken() {
    try {
        localStorage.removeItem(PRD_TOKEN_KEY);
    } catch {
        /* ignore */
    }
}

/**
 * Prefijos/keys de localStorage que NO deben borrarse en los `clear()` del flujo
 * de login (NO en logout). Preservarlos permite que MSAL conserve su cache
 * (incluido el refresh token) y pueda renovar el ID token silenciosamente con
 * acquireTokenSilent, en vez de quedarse sin auth cuando el ID token stashed
 * expira (~1h). Sin esto, el refresh token se perdía en cada clear y no había
 * forma de renovar sin re-login.
 *
 * - `msal.` / clientId / `-login.windows.net-` → cache interna de MSAL (cuentas,
 *   access/refresh/id tokens). MSAL usa varios formatos de key según versión.
 * - PRD_TOKEN_KEY → el ID token stashed (fallback que ya se preservaba aparte).
 * - userRoles → ya se preservaba en Session.setSessionCookie.
 */
// Keys propias de la app que siempre se preservan.
const PRESERVED_APP_KEYS = ['userRoles', PRD_TOKEN_KEY];

// Fragmentos que identifican entradas PERSISTENTES de la cache de MSAL (cuentas
// y tokens de larga vida). Estas son las que hay que conservar para que
// acquireTokenSilent pueda renovar el ID token.
//
// Formato real observado de las keys de MSAL en localStorage (B2C):
//   msal.<clientId>.active-account-filters        → cuenta activa (CLAVE: sin
//                                                    esto getAllAccounts()=0 y no
//                                                    se puede renovar)
//   <guid>-b2c_...-b2clogin.com-idtoken-<clientId>----
//   <guid>-b2c_...-b2clogin.com-refreshtoken-<clientId>----
//   <guid>-b2c_...-b2clogin.com-accesstoken-<clientId>-...
//   <guid>-...-account-<clientId>----              → registro de la cuenta
// Por eso incluimos tanto los prefijos `msal.` como los substrings de token y el
// dominio `b2clogin.com`.
const MSAL_PERSISTENT_SUBSTRINGS = [
    'msal.',
    'b2clogin.com',
    '-accesstoken-',
    '-refreshtoken-',
    '-idtoken-',
    '-account-',
    '-appmetadata-',
    'active-account-filters',
];

// Fragmentos de entradas TEMPORALES de MSAL ligadas a una interacción de
// redirect en curso. NUNCA se deben preservar: si sobreviven a un clear quedan
// "huérfanas" y el siguiente handleRedirectPromise falla con
// "no interaction in progress", rompiendo el login. Tienen prioridad sobre
// cualquier regla de preservación.
const MSAL_TEMPORARY_SUBSTRINGS = [
    'interaction.status',
    '.request.',
    '.request.state',
    '.request.params',
    '.request.origin',
    '.temporary',
    'nonce',
    'verifier',
    'authority.',
];

function shouldPreserveKey(key) {
    if (!key) return false;

    // 1) Nunca preservar entradas temporales de interacción (prioridad máxima).
    if (MSAL_TEMPORARY_SUBSTRINGS.some((s) => key.includes(s))) return false;

    // 2) Keys propias de la app.
    if (PRESERVED_APP_KEYS.includes(key)) return true;

    // 3) Entradas persistentes de MSAL (cuentas + tokens de larga vida).
    if (MSAL_PERSISTENT_SUBSTRINGS.some((s) => key.includes(s))) return true;

    return false;
}

/**
 * Borra localStorage/sessionStorage preservando SOLO las keys necesarias para
 * mantener viva la sesión de MSAL (refresh token) y el flujo de la app.
 *
 * Úsese en los puntos del LOGIN que hoy hacen `localStorage.clear()` a secas
 * (Session.setSessionCookie, App.js). NO usar en logout: ahí sí hay que borrar
 * todo (ver removeSessionCookie).
 */
export function clearStoragePreservingAuth() {
    try {
        const preserved = {};
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (shouldPreserveKey(key)) {
                preserved[key] = localStorage.getItem(key);
            }
        }
        localStorage.clear();
        sessionStorage.clear();
        Object.keys(preserved).forEach((k) => {
            localStorage.setItem(k, preserved[k]);
        });
    } catch {
        // Si storage no está disponible, el flujo original (clear) igual aplicaba.
        try { localStorage.clear(); } catch { /* ignore */ }
        try { sessionStorage.clear(); } catch { /* ignore */ }
    }
}

/** Reads the stashed token; returns null if absent or (best-effort) expired. */
function readStoredToken() {
    try {
        const token = localStorage.getItem(PRD_TOKEN_KEY);
        if (!token) return null;
        // Best-effort expiry check (no signature verification — backend does that).
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp && Date.now() >= payload.exp * 1000) {
            localStorage.removeItem(PRD_TOKEN_KEY);
            return null;
        }
        return token;
    } catch {
        return null;
    }
}

/**
 * Returns a fresh B2C ID token for the active account, or null when the user
 * is not signed in / a token cannot be acquired silently.
 *
 * We use the ID token (scopes stay empty per authConfig.loginRequest, so B2C
 * issues an ID token whose `aud` is the SPA client id). MSAL keeps the token in
 * its in-memory/cache layer and refreshes it silently — the raw token is never
 * persisted by this app. See the agent-authorizer backend, which validates the
 * token signature against the B2C JWKS.
 *
 * @returns {Promise<string|null>} the ID token, or null if unavailable.
 */
export async function getIdToken() {
    // TEMP DIAGNOSTIC — remove once the token flow is confirmed.
    const DBG = '[msal]';

    const activeAccount = msalInstance.getActiveAccount();
    const allAccounts = msalInstance.getAllAccounts();
    const account = activeAccount || allAccounts[0];

    console.log(DBG, 'getIdToken()', {
        hasActiveAccount: Boolean(activeAccount),
        allAccountsCount: allAccounts.length,
        accountUsername: account ? account.username : null,
    });

    // Preferred path: MSAL still has an account → acquire (and auto-refresh).
    if (account) {
        try {
            const result = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account,
            });
            console.log(DBG, 'acquireTokenSilent OK', {
                hasIdToken: Boolean(result.idToken),
                idTokenLen: result.idToken ? result.idToken.length : 0,
                claims: safeDecodeClaims(result.idToken),
            });
            if (result.idToken) {
                storePrdIdToken(result.idToken); // refresh the stash
                return result.idToken;
            }
        } catch (e) {
            console.warn(DBG, 'acquireTokenSilent FAILED, will try stored token', {
                name: e && e.name,
                errorCode: e && e.errorCode,
                message: e && e.message,
            });
        }
    }

    // Fallback (Option A): MSAL account was wiped by the Talisis login's
    // localStorage/sessionStorage clear, so use the token AuthScreen stashed at
    // login time. Valid until it expires (~1h); after that this returns null and
    // the request goes unauthenticated (backend is still in `legacy` for now).
    const stored = readStoredToken();
    console.log(DBG, 'stored token fallback', {
        hasStored: Boolean(stored),
        claims: stored ? safeDecodeClaims(stored) : null,
    });
    return stored;
}

/**
 * TEMP DIAGNOSTIC: decode a JWT payload WITHOUT verifying (just to eyeball
 * aud/tfp/exp/oid in the console). Never trust this for auth — the backend
 * verifies the signature.
 */
function safeDecodeClaims(jwt) {
    try {
        const payload = JSON.parse(atob(jwt.split('.')[1]));
        return {
            aud: payload.aud,
            tfp: payload.tfp,
            iss: payload.iss,
            oid: payload.oid,
            exp: payload.exp,
            hasEmails: Array.isArray(payload.emails),
        };
    } catch {
        return null;
    }
}
